import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { IndexeddbPersistence } from 'y-indexeddb';
import { yCollab, yUndoManagerKeymap } from 'y-codemirror.next';
import { keymap } from '@codemirror/view';
import type { Extension } from '@codemirror/state';
import { selectPresenceForFile } from './presence';

export interface CollabUser {
  name: string;
  color: string;
  colorLight: string;
}

export interface CollabOptions {
  wsUrl: string;
  roomName: string;
  user: CollabUser;
  token?: string;
  onUsersChange?: (users: CollabUserState[]) => void;
  onSynced?: (synced: boolean) => void;
  onConnectionStatus?: (status: string) => void;
}

export interface CollabUserState {
  clientId: number;
  name: string;
  color: string;
  currentFile?: string;
}

export interface CollabInstance {
  ydoc: Y.Doc;
  provider: WebsocketProvider;
  persistence: IndexeddbPersistence | null;
  extensions: Extension[];
  destroy: () => void;
  getSnapshot: () => Uint8Array;
  getText: () => string;
}

// Generate a random pastel color for a user
const userColors = [
  { color: '#FF6B6B', light: '#FF6B6B33' },
  { color: '#4ECDC4', light: '#4ECDC433' },
  { color: '#45B7D1', light: '#45B7D133' },
  { color: '#96CEB4', light: '#96CEB433' },
  { color: '#FFEAA7', light: '#FFEAA733' },
  { color: '#DDA0DD', light: '#DDA0DD33' },
  { color: '#98D8C8', light: '#98D8C833' },
  { color: '#F7DC6F', light: '#F7DC6F33' },
  { color: '#BB8FCE', light: '#BB8FCE33' },
  { color: '#85C1E9', light: '#85C1E933' },
];

export function getRandomUserColor(): { color: string; colorLight: string } {
  const c = userColors[Math.floor(Math.random() * userColors.length)];
  return { color: c.color, colorLight: c.light };
}

export function createCollaboration(options: CollabOptions): CollabInstance {
  const ydoc = new Y.Doc();
  const ytext = ydoc.getText('codemirror');

  // WebSocket provider for server sync
  const wsParams: Record<string, string> = {};
  if (options.token) {
    wsParams['auth'] = options.token;
  }

  const provider = new WebsocketProvider(
    options.wsUrl,
    options.roomName,
    ydoc,
    {
      connect: true,
      params: wsParams,
      maxBackoffTime: 10000,
    }
  );

  // Local IndexedDB persistence for offline resilience
  let persistence: IndexeddbPersistence | null = null;
  try {
    persistence = new IndexeddbPersistence(options.roomName, ydoc);
    persistence.on('synced', () => {
      console.log(`[Collab] IndexedDB synced for ${options.roomName}`);
    });
  } catch (e) {
    console.warn('[Collab] IndexedDB persistence unavailable:', e);
  }

  // Set awareness state
  const awareness = provider.awareness;
  awareness.setLocalStateField('user', {
    name: options.user.name,
    color: options.user.color,
    colorLight: options.user.colorLight,
  });

  // Track connected users
  if (options.onUsersChange) {
    const updateUsers = () => {
      const localFile = awareness.getLocalState()?.currentFile as
        | string
        | undefined;
      const states = Array.from(awareness.getStates().entries());
      const allUsers: CollabUserState[] = states
        .filter(([, state]) => state.user)
        .map(([clientId, state]) => ({
          clientId,
          name: state.user.name,
          color: state.user.color,
          currentFile: state.currentFile,
        }));
      // Scope presence to collaborators viewing the SAME document (file) as the
      // local user — awareness is per-project room, but presence is per-file.
      options.onUsersChange!(selectPresenceForFile(allUsers, localFile));
    };

    awareness.on('change', updateUsers);
    // Initial update
    setTimeout(updateUsers, 100);
  }

  // Track sync status
  if (options.onSynced) {
    provider.on('synced', (event: { synced: boolean }) => {
      options.onSynced!(event.synced);
    });
  }

  // Track connection status
  if (options.onConnectionStatus) {
    provider.on('status', (event: { status: string }) => {
      options.onConnectionStatus!(event.status);
    });
  }

  // CodeMirror extensions for collaboration
  const undoManager = new Y.UndoManager(ytext);
  const extensions: Extension[] = [
    yCollab(ytext, awareness, { undoManager }),
    keymap.of(yUndoManagerKeymap),
  ];

  const destroy = () => {
    provider.disconnect();
    provider.destroy();
    if (persistence) {
      persistence.destroy();
    }
    ydoc.destroy();
  };

  const getSnapshot = (): Uint8Array => {
    return Y.encodeStateAsUpdate(ydoc);
  };

  const getText = (): string => {
    return ytext.toString();
  };

  return {
    ydoc,
    provider,
    persistence,
    extensions,
    destroy,
    getSnapshot,
    getText,
  };
}

// Set the current file for awareness (so other users see which file you're editing)
export function setCurrentFile(provider: WebsocketProvider, filePath: string): void {
  provider.awareness.setLocalStateField('currentFile', filePath);
}
